import { AI_HELPER_CHANGE_REQUEST_AUTHOR } from "@corca/tiptap";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

const baseButtonClass =
  "flex select-none items-center justify-center rounded-full pl-[10px] pr-[11px] py-[5px] font-sans text-[16px] font-normal leading-none tracking-[-0.02em] transition-colors sm:flex-1";

const disabledButtonClass =
  "cursor-not-allowed bg-gray-100/80 text-gray-500 opacity-80 dark:bg-gray-900/80 dark:text-gray-400";

const rejectButtonClass =
  "bg-red-100/90 text-gray-800 hover:bg-red-200 active:translate-y-[1px] active:bg-red-300 dark:bg-red-950 dark:text-red-100 dark:hover:bg-red-900 dark:active:bg-red-800";

const approveButtonClass =
  "bg-blue-100/90 text-gray-900 hover:bg-blue-200 active:translate-y-[1px] active:bg-blue-300 dark:bg-blue-950 dark:text-blue-100 dark:hover:bg-blue-900 dark:active:bg-blue-800";

export const ChangeRequestWidget = observer(function ChangeRequestWidget({
  author,
  pending = false,
  onReject,
  onApprove,
}: {
  author?: string;
  pending?: boolean;
  onReject: () => void;
  onApprove: () => void;
}) {
  const t = useTranslations("doc.changeRequest");
  const { chats } = useStore();
  const disabled = pending || (chats?.isBotGeneratingForCurrentDoc ?? false);

  return (
    <div
      contentEditable={false}
      className="change-request-widget ml-[21px] flex items-center justify-between gap-3 pl-4 pr-0 pb-0 pt-1 text-[14px] text-gray-500 dark:text-gray-400 sm:ml-0 sm:flex-col sm:items-stretch sm:gap-2"
    >
      <span className="mb-[6px]">
        {author === AI_HELPER_CHANGE_REQUEST_AUTHOR && <Icon name="AiHelper" />}
      </span>
      <div className="flex shrink-0 items-center gap-2.5 -translate-y-[4px] -translate-x-[2px] sm:w-full">
        <button
          type="button"
          disabled={disabled}
          className={twMerge(baseButtonClass, disabled ? disabledButtonClass : rejectButtonClass)}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (disabled) return;
            onReject();
          }}
        >
          {t("undo")}
        </button>
        <button
          type="button"
          disabled={disabled}
          className={twMerge(baseButtonClass, disabled ? disabledButtonClass : approveButtonClass)}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (disabled) return;
            onApprove();
          }}
        >
          {t("approve")}
        </button>
      </div>
    </div>
  );
});
